import React, { useEffect } from "react";
import "leaflet/dist/leaflet.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import MapHub from "@/pages/MapHub";
import ReportCreate from "@/pages/ReportCreate";
import ReportDetail from "@/pages/ReportDetail";
import Profile from "@/pages/Profile";
import PositiveStories from "@/pages/PositiveStories";
import Leaderboard from "@/pages/Leaderboard";
import Donate from "@/pages/Donate";
import MissionPage from "@/pages/MissionPage";
import AboutPage from "@/pages/AboutPage";
import AdminContentPages from "@/pages/AdminContentPages";
import PaymentResult from "@/pages/PaymentResult";
import AdminDashboard from "@/pages/AdminDashboard";
import Settings from "@/pages/Settings";
import PasswordReset from "@/pages/PasswordReset";
import UrgentAlertCreate from "@/pages/UrgentAlertCreate";
import UrgentAlertDetail from "@/pages/UrgentAlertDetail";
import EventCreate from "@/pages/EventCreate";
import EventDetail from "@/pages/EventDetail";
import EventAreaEdit from "@/pages/EventAreaEdit";
import EventsHistoryPage from "@/pages/EventsHistoryPage";
import OnboardingTour from "@/components/OnboardingTour";
import usePushRegistration from "@/lib/usePushRegistration";
import NotificationsPage from "@/pages/NotificationsPage";
import PublicReport from "@/pages/PublicReport";
import ReforestationPage from "@/pages/ReforestationPage";
import { GuestGateProvider } from "@/components/GuestGate";
import { AssistantProvider } from "@/components/ContextAssistant";
import MarketplacePage from "@/pages/MarketplacePage";
import MarketplaceCreate from "@/pages/MarketplaceCreate";
import MarketplaceDetail from "@/pages/MarketplaceDetail";
import MyListings from "@/pages/MyListings";
import Following from "@/pages/Following";
import { MarketplaceUnreadProvider } from "@/lib/marketplaceUnread";
import { ChatUnreadProvider } from "@/lib/chatUnread";
import AdminPasswordReset from "@/pages/AdminPasswordReset";
import LegalPage from "@/pages/LegalPage";
import AccountDeletionPage from "@/pages/AccountDeletionPage";
import CompleteProfileModal from "@/components/CompleteProfileModal";
import ChatsPage from "@/pages/ChatsPage";
import ChatDetail from "@/pages/ChatDetail";
import AppFooter from "@/components/AppFooter";

function AppRouter() {
  const location = useLocation();
  // iter82 — Web Push token registration runs once as soon as an
  // authenticated user is available. Dormant when Firebase Web config is
  // missing (e.g. Preview) so it never blocks anonymous browsing.
  usePushRegistration();
  // Lotto 16 — PWA installability: always register the service worker at
  // "/" even for anonymous visitors and when Firebase config is missing.
  // Chrome/Edge require the SW to expose a `fetch` handler to mark the
  // app installable. Firebase-messaging-sw.js already provides one, we
  // just need to ensure the file is registered even when push is dormant.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    // Skip if usePushRegistration is going to register the same URL soon.
    const alreadyControlled = !!navigator.serviceWorker.controller;
    if (alreadyControlled) return;
    navigator.serviceWorker
      .register("/firebase-messaging-sw.js", { scope: "/" })
      .catch(() => { /* silent — installability still fine on next reload */ });
  }, []);
  // Handle OAuth callback (session_id in hash) synchronously during render
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/map" element={<MapHub />} />
      <Route path="/report/new" element={<ReportCreate />} />
      <Route path="/report/:id" element={<ReportDetail />} />
      <Route path="/profile/:id" element={<Profile />} />
      <Route path="/stories" element={<PositiveStories />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/donate" element={<Donate />} />
      <Route path="/payment/success" element={<PaymentResult success />} />
      <Route path="/payment/cancel" element={<PaymentResult success={false} />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/content-pages" element={<AdminContentPages />} />
      <Route path="/missione" element={<MissionPage />} />
      <Route path="/chi-siamo" element={<AboutPage />} />
      <Route path="/privacy" element={<LegalPage slug="privacy" titleKey="legal_privacy_title" />} />
      <Route path="/terms" element={<LegalPage slug="terms" titleKey="legal_terms_title" />} />
      <Route path="/community-rules" element={<LegalPage slug="community-rules" titleKey="legal_community_title" />} />
      <Route path="/marketplace-rules" element={<LegalPage slug="marketplace-rules" titleKey="legal_marketplace_title" />} />
      <Route path="/legal-info" element={<LegalPage slug="legal-info" titleKey="legal_info_title" />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/password-reset" element={<PasswordReset />} />
      <Route path="/urgent/new" element={<UrgentAlertCreate />} />
      <Route path="/urgent/:id" element={<UrgentAlertDetail />} />
      <Route path="/events/new" element={<EventCreate />} />
      <Route path="/events/history" element={<EventsHistoryPage />} />
      <Route path="/events/:id" element={<EventDetail />} />
      <Route path="/events/:id/area" element={<EventAreaEdit />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/r/:id" element={<PublicReport />} />
      <Route path="/reforestation" element={<ReforestationPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/new" element={<MarketplaceCreate />} />
      <Route path="/marketplace/:id" element={<MarketplaceDetail />} />
      <Route path="/me/marketplace" element={<MyListings />} />
      <Route path="/me/following" element={<Following />} />
      <Route path="/admin/reset" element={<AdminPasswordReset />} />
      <Route path="/chats" element={<ChatsPage />} />
      <Route path="/chats/:id" element={<ChatDetail />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <GuestGateProvider>
              <AssistantProvider>
                <MarketplaceUnreadProvider>
                  <ChatUnreadProvider>
                    <AppRouter />
                    <AppFooter />
                    <OnboardingTour />
                    <CompleteProfileModal />
                    <Toaster position="top-right" richColors />
                  </ChatUnreadProvider>
                </MarketplaceUnreadProvider>
              </AssistantProvider>
            </GuestGateProvider>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
